import { LitElement, html, css, unsafeCSS } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { keyed } from 'lit/directives/keyed.js';
import { RDPropsetContainer } from '../Container/PropContainer.js';
import Sortable from 'sortablejs';

import styles from './Tags.css?raw';

export class propTags extends RDPropsetContainer {
  static styles = [
    super.styles,
    css`
      ${unsafeCSS(styles)}
    `,
  ];
  static properties = {
    disabled: { type: Boolean, reflect: true },
    onAdded: { type: Function },
    onRemoved: { type: Function },
    onInput: { type: Function },
    onDragged: { type: Function },
    canDrag: { type: Boolean },
    canDelete: { type: Boolean },
    canAdd: { type: Boolean },
    placeholder: { type: String },
  };
  constructor() {
    super();
    this.disabled = false;
    this.onInput = null;
    this.onAdded = null;
    this.onRemoved = null;
    this.onDragged = null;
    this.canDrag = true;
    this.canDelete = true;
    this.canAdd = true;
    this.splitter = '\n';
    this.placeholder = '';
    //this.items = [];
    this._listKey = 0; // Key to force complete re-render
  }
  get splitter() {
    if (!this._splitter) {
      return '\n';
    }
    return this._splitter;
  }
  set splitter(val) {
    const oldValue = this._splitter;
    this._splitter = val;
    this.requestUpdate('splitter', oldValue);
  }
  get value() {
    if (!this.splitter) {
      this.splitter = '\n';
    }
    return this.items.join(this.splitter);
  }
  set value(val) {
    const oldValue = this.value;
    if (typeof val === 'string') {
      this.items = val
        .split(this.splitter)
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
    } else if (Array.isArray(val)) {
      this.items = val;
    } else {
      this.items = [];
    }
    this.requestUpdate('value', oldValue);
  }
  get items() {
    return this._items || [];
  }
  set items(val) {
    const oldValue = this._items;
    this._items = Array.isArray(val) ? [...val] : [];
    this._forceUpdate();
    this.requestUpdate('items', oldValue);
  }
  _forceUpdate() {
    this._listKey = Math.random();
  }
  updated(changedProperties) {
    super.updated(changedProperties);

    // Re-initialize Sortable after complete re-render
    if (changedProperties.has('items') || changedProperties.has('canDrag')) {
      queueMicrotask(() => {
        this._setupSortable();
      });
    }

    // Re-initialize Sortable when disabled state changes
    if (changedProperties.has('disabled') && this._sortable) {
      this._sortable.option('disabled', this.disabled);
    }
  }
  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._sortable) {
      this._sortable.destroy();
      this._sortable = null;
    }
  }

  firstUpdated() {
    super.firstUpdated();
    this._setupSortable();
  }

  _setupSortable() {
    const list = this.shadowRoot.querySelector('.rd-prop-tags-list');
    if (this._sortable) {
      this._sortable.destroy();
    }

    this._sortable = Sortable.create(list, {
      animation: 150,
      disabled: this.disabled || !this.canDrag,
      draggable: 'li',
      onEnd: (evt) => {
        const oldIndex = evt.oldIndex;
        const newIndex = evt.newIndex;

        if (oldIndex === newIndex) return;

        // Create a new array with the reordered items
        const newItems = [...this.items];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);

        // Update items via setter to trigger full re-render
        // This rebuilds Lit's comment markers properly
        this.items = newItems;

        if (this.onDragged) {
          this.onDragged.bind(this)(oldIndex, newIndex);
        }
      },
    });
  }

  _handleKeyDown(e) {
    const input = e.target;
    if ((e.key === 'Enter' && this.splitter === '\n') || e.key === this.splitter) {
      e.preventDefault();
      this._addTag(input.value.trim());
      input.value = '';
      this._focusInput();
    }
    //else if (e.key === 'Backspace' && input.value === '' && this.items.length > 0) {
    //   this._removeTag(this.items[this.items.length - 1]);
    // }
  }
  _focusInput() {
    queueMicrotask(() => {
      let inp = this.shadowRoot.querySelector('.rd-prop-tags-input');
      if (inp) {
        inp.focus();
      }
    });
  }
  _handleBlur() {
    const input = this.shadowRoot.querySelector('.rd-prop-tags-input');
    if (input && input.value.trim()) {
      this._addTag(input.value.trim());
      input.value = '';
    }
  }
  _handleInput(e) {
    if (this.onInput) {
      this.onInput.bind(this)(e.target.value);
    }
  }

  _addTag(tag) {
    if (!tag || this.items.includes(tag)) return;

    // Create new array instead of mutating
    this.items = [...this.items, tag];

    if (this.onAdded) {
      this.onAdded.bind(this)(tag);
    }
  }

  _removeTag(index) {
    if (index < 0 || index >= this.items.length || this.disabled) return;
    // Create new array instead of mutating
    let removedTag = this.items[index];
    this.items = this.items.filter((_, i) => i !== index);

    if (this.onRemoved) {
      this.onRemoved.bind(this)(removedTag, index);
    }
  }
  _renderTags() {
    return this.items.map(
      (tag, index) => html`
        <li class="rd-prop-tag" data-id="${index}">
          <span class="rd-prop-tag-label ${this.canDrag && !this.disabled ? 'grab' : ''}"><slot name="tag-${index}">${tag}</slot></span>
          ${this.canDelete ? html`<span class="rd-prop-tag-remove" style="${this.disabled ? 'pointer-events: none; opacity: 0.6;' : ''}" @click=${() => this._removeTag(index)}>&times;</span>` : ''}
        </li>
      `
    );
  }
  _renderTagsValues() {
    let allTags = this.querySelectorAll('.tags');
    allTags.forEach((tagElement) => tagElement.remove());
    this.items.forEach((tag, index) => {
      const tagElement = document.createElement('span');
      tagElement.setAttribute('slot', `tag-${index}`);
      tagElement.classList.add('tags');
      tagElement.innerHTML = tag;
      this.appendChild(tagElement);
    });
  }
  renderBody() {
    return html`
      ${keyed(
        this._listKey,
        html`
          <div class="rd-prop-tags-container" tabindex="-1" @click=${() => this._focusInput()}>
            <ul class="rd-prop-tags-list">
              ${this._renderTags()}
              ${this.canAdd
                ? html`
                    <div class="rd-prop-tags-input-container">
                      <input placeholder=${this.placeholder} type="text" class="rd-prop-tags-input" ?disabled=${this.disabled} @keydown=${(e) => this._handleKeyDown(e)} @blur=${() => this._handleBlur()} @input=${(e) => this._handleInput(e)} />
                    </div>
                  `
                : ''}
            </ul>
          </div>
        `
      )}
    `;
  }
}
if (!customElements.get('rd-prop-tags')) {
  customElements.define('rd-prop-tags', propTags);
}
